import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { useNotificationStore } from "@/store/notificationStore";
import { ProfileAvatar } from "./ProfileAvatar";
import type { AppNotification } from "@/types/api";

interface NotificationBellProps {
  isLight: boolean;
  isGlassy: boolean;
}

const POLL_INTERVAL_MS = 30_000;

function notificationLink(n: AppNotification): string | null {
  if (n.type === "chat_message" && n.data?.conversationId) {
    return `/member/chat?conversation=${encodeURIComponent(n.data.conversationId)}`;
  }
  if (n.type === "friend_request_received" || n.type === "friend_request_accepted") {
    return "/member/friends";
  }
  // Generic in-app link (e.g. surprise_party_invite / surprise_party_ready); only same-site paths.
  if (typeof n.data?.path === "string" && n.data.path.startsWith("/") && !n.data.path.startsWith("//")) {
    return n.data.path;
  }
  return null;
}

/**
 * Relative durations ("2h ago") are timezone-agnostic — only elapsed time, not clock time.
 * The >=7 day fallback shows an absolute date, which comes pre-formatted from the API in the
 * viewer's account timezone (`n.date`), not computed here.
 */
function timeAgo(n: AppNotification): string {
  if (!n.createdAt) return "";
  const diffMs = Date.now() - new Date(n.createdAt).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return n.date ?? "";
}

export function NotificationBell({ isLight, isGlassy }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { notifications, unreadCount, loading, initialize, refresh, refreshUnreadCount, markRead, markAllRead } =
    useNotificationStore();

  useEffect(() => {
    void initialize();
    const interval = setInterval(() => {
      void refreshUnreadCount();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [initialize, refreshUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    // Side effects don't belong inside a setState updater — React calls updater functions
    // as part of reconciliation, so `refresh()`'s own synchronous `set()` was firing during
    // that phase ("Cannot update a component while rendering a different component").
    const next = !open;
    setOpen(next);
    if (next) void refresh();
  };

  const handleItemClick = (n: AppNotification) => {
    void markRead(n.id);
    setOpen(false);
    const link = notificationLink(n);
    if (link) navigate(link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleToggle}
        aria-label="Notifications"
        className={`relative w-9 h-9 rounded-full flex items-center justify-center border transition-colors cursor-pointer ${
          isGlassy
            ? "bg-white/[0.06] border-white/[0.1] text-white/80 hover:text-white hover:bg-white/[0.1]"
            : isLight
            ? "bg-gray-100 border-gray-200/60 text-gray-600 hover:text-gray-900 hover:bg-gray-200"
            : "bg-white/5 border-white/10 text-white/80 hover:text-white hover:bg-white/10"
        }`}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff3366] text-white text-[10px] flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute right-0 top-full z-50 mt-2 w-80 max-w-[90vw] ${
            isGlassy
              ? "bg-[#12121c]/80 border-white/[0.12] shadow-2xl shadow-purple-900/20 backdrop-blur-2xl"
              : isLight
              ? "bg-white/95 border-gray-200/80 shadow-xl shadow-black/10"
              : "bg-[#12121c]/95 border-white/[0.1] shadow-2xl shadow-black/50"
          } backdrop-blur-xl border rounded-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200`}
        >
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isLight ? "border-gray-200/60" : "border-white/[0.06]"}`}>
            <p className={`${isLight ? "text-gray-900" : "text-white"} text-sm font-semibold`}>Notifications</p>
            {unreadCount > 0 && (
              <button
                onClick={() => void markAllRead()}
                className={`flex items-center gap-1 text-[11px] font-medium transition-colors cursor-pointer ${
                  isLight ? "text-gray-500 hover:text-gray-900" : "text-gray-400 hover:text-white"
                }`}
              >
                <CheckCheck size={12} />
                Mark all as read
              </button>
            )}
          </div>

          <div className="max-h-[360px] overflow-y-auto custom-scrollbar">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <p className={`px-4 py-8 text-center text-sm ${isLight ? "text-gray-400" : "text-gray-500"}`}>
                No notifications yet
              </p>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left flex items-start gap-2 px-4 py-3 border-b last:border-b-0 transition-colors cursor-pointer ${
                    isLight ? "border-gray-100" : "border-white/[0.04]"
                  } ${
                    !n.read
                      ? isLight
                        ? "bg-[#ff3366]/[0.04] hover:bg-[#ff3366]/[0.08]"
                        : "bg-[#ff3366]/[0.06] hover:bg-[#ff3366]/[0.1]"
                      : isLight
                      ? "hover:bg-gray-100/80"
                      : "hover:bg-white/[0.06]"
                  }`}
                >
                  {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ff3366] shrink-0" />}
                  <div className={`w-8 h-8 rounded-full overflow-hidden shrink-0 ${n.read ? "ml-3.5" : ""}`}>
                    <ProfileAvatar
                      src={n.data?.senderAvatar}
                      name={n.title}
                      alt={n.title}
                      className="w-full h-full rounded-full"
                      isLight={isLight}
                      fallbackTextClassName="text-xs font-semibold"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${isLight ? "text-gray-900" : "text-white"}`}>{n.title}</p>
                    <p className={`text-xs mt-0.5 line-clamp-2 ${isLight ? "text-gray-500" : "text-gray-400"}`}>{n.body}</p>
                    <p className={`text-[10px] mt-1 ${isLight ? "text-gray-400" : "text-gray-600"}`}>{timeAgo(n)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
